import { useEffect, useState } from 'react';
import { X, Plus, Trash2, Loader2, CheckCircle2, AlertTriangle, Ticket, Camera, Copy, Calendar, Edit3 } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../context/LanguageContext';
import WaiverStampIcon from './WaiverStampIcon';
import DatePicker from './DatePicker';

const API = process.env.REACT_APP_BACKEND_URL;

// Global opener — pass { hold_id, order_id, order_num, buyer_name, buyer_email } to auto-link
let globalOpen = null;
export function openHHWaiver(ctx = {}) {
  if (globalOpen) globalOpen(ctx);
}

const newGuest = () => ({ name: '', date_of_birth: '', age: '', is_minor: false });
const todayStr = () => {
  const d = new Date();
  const yy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};
const nameMatches = (a, b) => (a || '').trim().toLowerCase().replace(/\s+/g, ' ') === (b || '').trim().toLowerCase().replace(/\s+/g, ' ');

// Localized helper
const T = (isJa) => ({
  title: isJa ? 'ハウンテッドハウス同意書' : 'Haunted House Waiver',
  required: isJa ? '入場前に必須' : 'Required Prior to Entry',
  leaderResponsibility: isJa
    ? 'グループリーダー1名がグループ全員の責任を代表して負うか、各ゲストにリンクを送って個別に署名してもらうことができます。'
    : 'One group leader can take responsibility for the whole group — or forward the waiver link so each guest signs on their own.',
  forwardLink: isJa ? 'リンクをコピー' : 'Copy Link to Forward',
  forwardCopied: isJa ? 'コピーしました！' : 'Copied — paste it to your guests',
  loading: isJa ? '読み込み中…' : 'Loading…',
  order: isJa ? 'チケット / 予約番号' : 'Ticket / Booking Number',
  orderHint: isJa
    ? 'メールに記載された注文番号を入力してください。分からない場合は「わからない」を押してください（スタッフが受付時にお名前で照合します）。'
    : "Enter the order number from your ticket email. Tap \"I don't know\" if you don't have it — staff will match you by name at check-in.",
  idk: isJa ? 'わからない' : "I don't know",
  participantInfo: isJa ? '参加者情報' : 'Participant Information',
  fullName: isJa ? '氏名 (グループリーダー)' : 'Full Name (Group Leader)',
  dob: isJa ? '生年月日' : 'Date of Birth',
  age: isJa ? '年齢' : 'Age',
  phone: isJa ? '電話番号' : 'Phone Number',
  email: isJa ? 'メールアドレス' : 'Email',
  ecName: isJa ? '緊急連絡先 氏名' : 'Emergency Contact Name',
  ecPhone: isJa ? '緊急連絡先 電話番号' : 'Emergency Contact Phone',
  additional: isJa ? '追加ゲスト（任意）' : 'Additional Guests (optional)',
  addNote: isJa
    ? 'あなたはリーダーとしてグループ全員のためにルールを確認し同意しています。18歳未満のゲストの参加もリーダーが代理承認します。'
    : "You (leader) accept on behalf of everyone. Anyone under 18 in the group is auto-approved by you as guardian.",
  addGuestBtn: isJa ? '追加' : 'Add',
  guestName: isJa ? "ゲストの氏名" : "Guest's full name",
  minorLabel: isJa ? '18歳未満（グループリーダーが代理承認）' : 'Under 18 — leader approves on their behalf',
  photoTitle: isJa ? '写真・動画 (任意)' : 'Photography & Video (optional)',
  photoIn: isJa ? '当日の写真・動画をプロモーション/SNSで使用することに同意します' : 'I consent to Oki-Escape using photos/videos of me for promotion & social media',
  photoOut: isJa ? '同意しません' : 'I do not consent',
  photoSkip: isJa ? '選択しない' : 'Skip',
  guardianTitle: isJa ? '保護者・法定代理人の同意（グループに18歳未満がいる場合）' : 'Parent / Legal Guardian Consent (only if group has minors)',
  guardianNote: isJa
    ? '未成年者と一緒に来場する保護者の情報を記入してください。あなたがそのまま保護者の場合はリーダー氏名と同じで構いません。'
    : "Fill in the details of the minor's parent or legal guardian. If YOU are the guardian, you may reuse the leader's name.",
  guardianName: isJa ? '保護者 氏名' : 'Parent/Guardian Full Name',
  guardianRel: isJa ? '続柄' : 'Relationship',
  guardianPhone: isJa ? '保護者 電話番号' : 'Parent/Guardian Phone',
  guardianSig: isJa ? '保護者 署名（氏名を再入力）' : 'Parent/Guardian Signature (retype name)',
  acknowledgeTitle: isJa ? '確認事項' : 'Acknowledgment',
  acceptAll: isJa
    ? '上記の警告・危険引受・ルール・免責をすべて読み、理解し、グループ全員を代表して自発的に同意します。'
    : 'I have read and understood the health warning, assumption of risk, rules, and release of liability — and voluntarily agree on behalf of every guest in my group.',
  ackRules: isJa
    ? '参加者ルールに同意し、グループ全員にルールを共有します。'
    : 'I agree to the participant rules and will share them with every guest in my group.',
  signatureTitle: isJa ? 'グループリーダー 署名' : 'Group Leader Signature',
  signaturePlaceholder: isJa ? '氏名を再入力（署名として）' : 'Retype your name as legal signature',
  signatureMismatch: isJa
    ? '署名は上記の氏名と完全に一致する必要があります'
    : 'Signature must exactly match the participant name above',
  todayLabel: isJa ? '日付' : 'Date',
  editBtn: isJa ? '編集' : 'Edit',
  confirmBtn: isJa ? '確認' : 'Confirm',
  submitBtn: isJa ? '同意して送信' : 'Sign & Submit',
  signing: isJa ? '送信中…' : 'Signing…',
  disabledMsg: isJa ? 'ハウンテッドハウス同意書は現在ご利用いただけません' : 'Haunted House waivers are currently disabled.',
  successTitle: isJa ? '同意書を受け付けました' : 'Waiver Signed',
  close: isJa ? '閉じる' : 'Close',
  requiredAsterisk: '*',
});

export default function HHWaiverModal() {
  const [open, setOpen] = useState(false);
  const [ctx, setCtx] = useState({});
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');

  const [orderInput, setOrderInput] = useState('');
  const [leaderName, setLeaderName] = useState('');
  const [leaderDob, setLeaderDob] = useState('');
  const [leaderAge, setLeaderAge] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [ecName, setEcName] = useState('');
  const [ecPhone, setEcPhone] = useState('');
  const [additional, setAdditional] = useState([]);
  const [photoConsent, setPhotoConsent] = useState('skip'); // 'in' | 'out' | 'skip'
  const [guardianName, setGuardianName] = useState('');
  const [guardianRel, setGuardianRel] = useState('');
  const [guardianPhone, setGuardianPhone] = useState('');
  const [guardianSig, setGuardianSig] = useState('');
  const [rulesOk, setRulesOk] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [signedAt, setSignedAt] = useState(todayStr());
  const [editingDate, setEditingDate] = useState(false);

  const { lang } = useLanguage();
  const isJa = lang === 'ja';
  const t = T(isJa);

  useEffect(() => {
    globalOpen = (c) => { setCtx(c || {}); setOpen(true); };
    return () => { globalOpen = null; };
  }, []);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/api/hh/waiver/config`).then(r => setCfg(r.data)).catch(() => {});
    setDone(null); setErr(''); setAccepted(false); setRulesOk(false);
    setOrderInput(ctx.order_num || ctx.hold_id || '');
    setLeaderName(ctx.buyer_name || '');
    setLeaderDob(''); setLeaderAge('');
    setSignature('');
    setSignedAt(todayStr());
    setEditingDate(false);
    setEmail(ctx.buyer_email || '');
    setPhone(ctx.buyer_phone || '');
    setEcName(''); setEcPhone('');
    setAdditional([]);
    setPhotoConsent('skip');
    setGuardianName(''); setGuardianRel(''); setGuardianPhone(''); setGuardianSig('');
  }, [open, ctx]);

  if (!open) return null;
  if (cfg && !cfg.enabled) {
    return (
      <div className="fixed inset-0 z-[10010] bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
        <div className="bg-neutral-950 border border-orange-500/30 rounded-2xl max-w-md p-6" onClick={e => e.stopPropagation()}>
          <p className="text-center text-white">{t.disabledMsg}</p>
        </div>
      </div>
    );
  }

  const legalText = cfg ? (isJa ? cfg.legal_text_ja : cfg.legal_text_en) : '';
  const orderIsLocked = !!(ctx.hold_id || ctx.order_id);
  const hasMinor = additional.some(g => g.is_minor);

  const addGuest = () => setAdditional(g => [...g, newGuest()]);
  const removeGuest = (i) => setAdditional(g => g.filter((_, idx) => idx !== i));
  const updateGuest = (i, patch) => setAdditional(g => g.map((x, idx) => idx === i ? { ...x, ...patch } : x));

  const canSubmit = () => {
    if (!accepted || !rulesOk) return false;
    if (!orderInput.trim()) return false;
    if (!leaderName.trim() || !signature.trim()) return false;
    if (!nameMatches(signature, leaderName)) return false;
    if (!email || !email.includes('@')) return false;
    if (!phone.trim() || !ecName.trim() || !ecPhone.trim()) return false;
    for (const g of additional) if (!g.name.trim()) return false;
    if (hasMinor) {
      if (!guardianName.trim() || !guardianRel.trim() || !guardianPhone.trim() || !guardianSig.trim()) return false;
      if (!nameMatches(guardianSig, guardianName)) return false;
    }
    return true;
  };

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const body = {
        leader_name: leaderName.trim(),
        leader_signature: signature.trim(),
        leader_date_of_birth: leaderDob || undefined,
        leader_age: leaderAge ? Number(leaderAge) : undefined,
        email: email.trim(),
        phone: phone.trim(),
        emergency_contact_name: ecName.trim(),
        emergency_contact_phone: ecPhone.trim(),
        photo_consent: photoConsent === 'in' ? true : (photoConsent === 'out' ? false : null),
        accepted: true,
        rules_acknowledged: rulesOk,
        additional_guests: additional.map(g => ({
          name: g.name.trim(),
          date_of_birth: g.date_of_birth || undefined,
          age: g.age ? Number(g.age) : undefined,
          is_minor: !!g.is_minor,
        })),
        // Ticket linkage — prefer context; otherwise use user-typed order # / hold id
        hold_id: ctx.hold_id || undefined,
        order_id: ctx.order_id || undefined,
        order_num: (ctx.order_num || orderInput || '').trim() || undefined,
        // Guardian block (only sent when a minor is in the group)
        guardian_full_name: hasMinor ? guardianName.trim() : undefined,
        guardian_relationship: hasMinor ? guardianRel.trim() : undefined,
        guardian_phone: hasMinor ? guardianPhone.trim() : undefined,
        guardian_signature: hasMinor ? guardianSig.trim() : undefined,
        signed_at_local: signedAt,
      };
      const r = await axios.post(`${API}/api/hh/waiver/submit`, body);
      setDone(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Something went wrong');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[10010] bg-black/70 flex items-center justify-center p-3 sm:p-6 overflow-y-auto" onClick={() => setOpen(false)}>
      <div onClick={e => e.stopPropagation()}
        className="bg-neutral-950 border-2 border-red-600/60 rounded-2xl w-full max-w-2xl my-auto overflow-hidden flex flex-col"
        style={{ boxShadow: '0 0 40px rgba(220,38,38,0.5)', maxHeight: 'calc(100vh - 24px)' }}
        data-testid="hh-waiver-modal">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-800 via-red-700 to-red-900 p-5 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-2">
            <WaiverStampIcon size={26} />
            <h3 className="font-heading text-lg sm:text-xl tracking-widest uppercase text-white"
              style={{ textShadow: '0 0 10px rgba(0,0,0,0.6)' }}>
              {t.title}
            </h3>
          </div>
          <button onClick={() => setOpen(false)} data-testid="hh-waiver-close"
            className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white hover:bg-black/60">
            <X size={16} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center space-y-4" data-testid="hh-waiver-success">
            <CheckCircle2 size={48} className="text-emerald-400 mx-auto" />
            <h4 className="font-heading text-lg tracking-widest uppercase">{t.successTitle}</h4>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">{done.message}</p>
            <div className="rounded-lg border border-border bg-black/40 p-3 text-xs font-mono inline-block">
              <div>ID: {done.waiver_id}</div>
              {done.order_num && <div>{isJa ? '注文' : 'Order'}: {done.order_num}</div>}
              <div>{done.guest_count} guest{done.guest_count === 1 ? '' : 's'}{done.minor_count > 0 ? ` · ${done.minor_count} minor${done.minor_count === 1 ? '' : 's'}` : ''}</div>
            </div>
            <button onClick={() => setOpen(false)} data-testid="hh-waiver-success-close"
              className="mt-2 bg-red-600 hover:bg-red-500 text-white font-heading tracking-widest uppercase text-xs px-6 py-2.5 rounded-full">
              {t.close}
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-5 overflow-y-auto">
            {/* Required-prior-to-entry banner */}
            <div className="rounded-lg border-2 border-red-600/60 bg-red-950/40 p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-400 mt-0.5 shrink-0" />
              <div className="text-xs flex-1">
                <p className="font-heading text-red-300 tracking-widest uppercase">{t.required}</p>
                <p className="text-red-200/80 mt-1">{t.leaderResponsibility}</p>
                <ForwardLinkButton
                  label={t.forwardLink}
                  copiedLabel={t.forwardCopied}
                  href={typeof window !== 'undefined'
                    ? `${window.location.origin}/haunted/waiver${(ctx.hold_id || ctx.order_num) ? `?order=${encodeURIComponent(ctx.hold_id || ctx.order_num)}` : ''}`
                    : ''}
                  color="red"
                />
              </div>
            </div>

            {/* Ticket / Booking Number — REQUIRED */}
            <Section title={t.order} icon={<Ticket size={13} />}>
              {orderIsLocked ? (
                <div className="rounded-lg border border-orange-500/40 bg-orange-950/30 px-3 py-2 text-sm font-mono text-orange-200 flex items-center gap-2">
                  <Ticket size={13} /> {ctx.order_num || ctx.hold_id}
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <input value={orderInput} onChange={e => setOrderInput(e.target.value)}
                      data-testid="hh-waiver-order-input"
                      placeholder={isJa ? '例: HH-2026-XXXX' : 'e.g. HH-2026-XXXX'}
                      className="flex-1 bg-black border border-neutral-700 focus:border-red-500 rounded-lg px-3 py-2 text-sm outline-none font-mono" />
                    <button type="button" onClick={() => setOrderInput('idk')}
                      data-testid="hh-waiver-order-idk"
                      className="shrink-0 font-heading text-[10px] tracking-widest uppercase bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-neutral-200 px-3 py-2 rounded-lg">
                      {t.idk}
                    </button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">{t.orderHint}</p>
                </>
              )}
            </Section>

            {/* Participant Info */}
            <Section title={t.participantInfo}>
              <TextField label={t.fullName + ' ' + t.requiredAsterisk} value={leaderName} onChange={setLeaderName}
                testId="hh-waiver-leader-name" placeholder={isJa ? '氏名' : 'Your full name'} />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground block mb-0.5">{t.dob}</label>
                  <DatePicker
                    value={leaderDob}
                    onChange={setLeaderDob}
                    placeholder={isJa ? '日付を選択' : 'Pick a date'}
                    testId="hh-waiver-leader-dob"
                    theme="red"
                    dobMode
                    locale={isJa ? 'ja' : 'en'}
                  />
                </div>
                <TextField label={t.age} type="number" value={leaderAge} onChange={setLeaderAge} testId="hh-waiver-leader-age" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <TextField label={t.phone + ' ' + t.requiredAsterisk} type="tel" value={phone} onChange={setPhone} testId="hh-waiver-phone" />
                <TextField label={t.email + ' ' + t.requiredAsterisk} type="email" value={email} onChange={setEmail}
                  testId="hh-waiver-email" placeholder="you@example.com" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <TextField label={t.ecName + ' ' + t.requiredAsterisk} value={ecName} onChange={setEcName} testId="hh-waiver-ec-name" />
                <TextField label={t.ecPhone + ' ' + t.requiredAsterisk} type="tel" value={ecPhone} onChange={setEcPhone} testId="hh-waiver-ec-phone" />
              </div>
            </Section>

            {/* Legal text (scrollable snapshot) */}
            <div className="rounded-lg border border-red-500/30 bg-black/60 p-3 text-[11px] leading-relaxed text-red-100/85 max-h-56 overflow-y-auto whitespace-pre-line font-sans"
              data-testid="hh-waiver-legal-text">
              {legalText || t.loading}
            </div>

            {/* Additional guests */}
            <Section title={t.additional}>
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-muted-foreground flex-1">{t.addNote}</p>
                <button type="button" onClick={addGuest} data-testid="hh-waiver-add-guest"
                  className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-red-600 hover:bg-red-500 text-white px-2.5 py-1 rounded-full">
                  <Plus size={11} /> {t.addGuestBtn}
                </button>
              </div>
              {additional.length > 0 && (
                <div className="space-y-2">
                  {additional.map((g, i) => (
                    <div key={i} className="rounded-lg border border-neutral-700 bg-black/40 p-3 space-y-2" data-testid={`hh-waiver-guest-${i}`}>
                      <div className="flex items-center gap-2">
                        <input value={g.name} onChange={e => updateGuest(i, { name: e.target.value })}
                          data-testid={`hh-waiver-guest-name-${i}`}
                          placeholder={t.guestName}
                          className="flex-1 bg-black border border-neutral-700 focus:border-red-500 rounded-lg px-3 py-2 text-sm outline-none" />
                        <button type="button" onClick={() => removeGuest(i)} data-testid={`hh-waiver-remove-guest-${i}`}
                          className="w-9 h-9 rounded-lg border border-neutral-700 hover:border-red-500 hover:text-red-400 text-neutral-500 flex items-center justify-center">
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <DatePicker
                          value={g.date_of_birth}
                          onChange={(v) => updateGuest(i, { date_of_birth: v })}
                          placeholder={isJa ? '生年月日' : 'Date of Birth'}
                          testId={`hh-waiver-guest-dob-${i}`}
                          theme="red"
                          dobMode
                          locale={isJa ? 'ja' : 'en'}
                        />
                        <input type="number" value={g.age} onChange={e => updateGuest(i, { age: e.target.value })}
                          data-testid={`hh-waiver-guest-age-${i}`}
                          placeholder={t.age}
                          className="bg-black border border-neutral-700 focus:border-red-500 rounded-lg px-2 py-1.5 text-xs outline-none" />
                      </div>
                      <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none">
                        <input type="checkbox" checked={g.is_minor} onChange={e => updateGuest(i, { is_minor: e.target.checked })}
                          data-testid={`hh-waiver-minor-${i}`}
                          className="w-4 h-4 accent-red-500" />
                        <span>{t.minorLabel}</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* Photo consent — optional */}
            <Section title={t.photoTitle} icon={<Camera size={13} />}>
              <div className="space-y-1.5">
                <PhotoRadio value="in"   current={photoConsent} onChange={setPhotoConsent} label={t.photoIn} />
                <PhotoRadio value="out"  current={photoConsent} onChange={setPhotoConsent} label={t.photoOut} />
                <PhotoRadio value="skip" current={photoConsent} onChange={setPhotoConsent} label={t.photoSkip} />
              </div>
            </Section>

            {/* Guardian block only when a minor is in the group */}
            {hasMinor && (
              <Section title={t.guardianTitle}>
                <p className="text-[10px] text-muted-foreground">{t.guardianNote}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <TextField label={t.guardianName + ' ' + t.requiredAsterisk} value={guardianName} onChange={setGuardianName} testId="hh-waiver-guardian-name" />
                  <TextField label={t.guardianRel + ' ' + t.requiredAsterisk} value={guardianRel} onChange={setGuardianRel} testId="hh-waiver-guardian-rel"
                    placeholder={isJa ? '例: 母、父、叔父' : 'e.g. Mother, Father, Uncle'} />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <TextField label={t.guardianPhone + ' ' + t.requiredAsterisk} type="tel" value={guardianPhone} onChange={setGuardianPhone} testId="hh-waiver-guardian-phone" />
                  <TextField label={t.guardianSig + ' ' + t.requiredAsterisk} value={guardianSig} onChange={setGuardianSig} testId="hh-waiver-guardian-sig"
                    placeholder={isJa ? '氏名を再入力' : 'Retype full name'} />
                </div>
                {guardianSig && guardianName && !nameMatches(guardianSig, guardianName) && (
                  <p className="text-[10px] text-red-400" data-testid="hh-waiver-guardian-sig-mismatch">
                    <AlertTriangle size={10} className="inline mr-1" />{t.signatureMismatch}
                  </p>
                )}
              </Section>
            )}

            {/* Acknowledgment */}
            <Section title={t.acknowledgeTitle}>
              <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={rulesOk} onChange={e => setRulesOk(e.target.checked)}
                  data-testid="hh-waiver-rules-ok" className="mt-0.5 w-4 h-4 accent-red-500" />
                <span>{t.ackRules}</span>
              </label>
              <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
                <input type="checkbox" checked={accepted} onChange={e => setAccepted(e.target.checked)}
                  data-testid="hh-waiver-accept" className="mt-0.5 w-4 h-4 accent-red-500" />
                <span>{t.acceptAll}</span>
              </label>
            </Section>

            {/* Leader signature + today's date */}
            <Section title={t.signatureTitle}>
              <input value={signature} onChange={e => setSignature(e.target.value)}
                data-testid="hh-waiver-signature"
                placeholder={t.signaturePlaceholder}
                className={`w-full bg-black border-2 rounded-lg px-3 py-3 text-base outline-none font-mono ${
                  signature && !nameMatches(signature, leaderName)
                    ? 'border-red-500 focus:border-red-400'
                    : 'border-red-600/60 focus:border-red-400'
                }`} />
              {signature && !nameMatches(signature, leaderName) && (
                <p className="text-[10px] text-red-400" data-testid="hh-waiver-sig-mismatch">
                  <AlertTriangle size={10} className="inline mr-1" />{t.signatureMismatch}
                </p>
              )}
              {/* Date — auto-filled today, one-tap edit */}
              <DateConfirm
                label={t.todayLabel}
                value={signedAt}
                editing={editingDate}
                onEdit={() => setEditingDate(true)}
                onChange={(v) => setSignedAt(v)}
                onConfirm={() => setEditingDate(false)}
                editLabel={t.editBtn}
                confirmLabel={t.confirmBtn}
                testId="hh-waiver-date"
                color="red"
              />
            </Section>

            {err && <p className="text-xs text-red-400 text-center" data-testid="hh-waiver-error">{err}</p>}

            <button onClick={submit} disabled={!canSubmit() || busy} data-testid="hh-waiver-submit"
              className="w-full bg-red-600 hover:bg-red-500 text-white font-heading tracking-widest uppercase text-sm py-3 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-40">
              {busy ? <Loader2 className="animate-spin" size={14} /> : <WaiverStampIcon size={22} />}
              {busy ? t.signing : t.submitBtn}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- helpers --- */
function Section({ title, icon, children }) {
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5 font-heading text-[10px] tracking-widest uppercase text-muted-foreground border-b border-border pb-1">
        {icon}
        <span>{title}</span>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function TextField({ label, value, onChange, type = 'text', testId, placeholder }) {
  return (
    <div>
      <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground block mb-0.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)}
        data-testid={testId} placeholder={placeholder}
        className="w-full bg-black border border-neutral-700 focus:border-red-500 rounded-lg px-3 py-2 text-sm outline-none" />
    </div>
  );
}

function PhotoRadio({ value, current, onChange, label }) {
  return (
    <label className="flex items-start gap-2 text-xs cursor-pointer select-none">
      <input type="radio" checked={current === value} onChange={() => onChange(value)}
        data-testid={`hh-waiver-photo-${value}`}
        className="mt-0.5 w-4 h-4 accent-red-500" />
      <span>{label}</span>
    </label>
  );
}

/** Copies a waiver deep-link so the leader can text/share it with guests who prefer to sign themselves. */
function ForwardLinkButton({ label, copiedLabel, href, color = 'red' }) {
  const [copied, setCopied] = useState(false);
  if (!href) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(href);
    } catch {
      // Fallback: open in a new tab so the leader can copy manually
      window.open(href, '_blank', 'noopener');
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };
  const cls = color === 'orange'
    ? 'bg-orange-900/60 border-orange-500/60 text-orange-200 hover:bg-orange-800/70'
    : 'bg-red-900/60 border-red-500/60 text-red-200 hover:bg-red-800/70';
  return (
    <button type="button" onClick={onCopy} data-testid="waiver-forward-link"
      className={`mt-2 inline-flex items-center gap-1.5 font-heading text-[10px] tracking-widest uppercase border px-2.5 py-1 rounded-full transition-colors ${cls}`}>
      {copied ? <CheckCircle2 size={11} /> : <Copy size={11} />}
      {copied ? copiedLabel : label}
    </button>
  );
}

/** Auto-filled today's date with a one-tap Edit / Confirm toggle */
function DateConfirm({ label, value, editing, onEdit, onChange, onConfirm, editLabel, confirmLabel, testId, color = 'red' }) {
  const isOrange = color === 'orange';
  const iconCls = isOrange ? 'text-orange-300' : 'text-red-300';
  const inputFocus = isOrange ? 'focus:border-orange-500' : 'focus:border-red-500';
  const confirmBtnCls = isOrange
    ? 'bg-orange-600 hover:bg-orange-500 text-black'
    : 'bg-red-600 hover:bg-red-500 text-white';
  return (
    <div className="rounded-lg border border-neutral-700 bg-black/40 p-3 flex items-center gap-2">
      <Calendar size={14} className={iconCls} />
      <span className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground">{label}</span>
      {editing ? (
        <>
          <input
            type="date"
            value={value}
            onChange={e => onChange(e.target.value)}
            data-testid={`${testId}-input`}
            className={`flex-1 bg-black border border-neutral-700 ${inputFocus} rounded-md px-2 py-1 text-sm outline-none font-mono`}
          />
          <button type="button" onClick={onConfirm} data-testid={`${testId}-confirm`}
            className={`inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase ${confirmBtnCls} px-2.5 py-1 rounded-full`}>
            <CheckCircle2 size={11} /> {confirmLabel}
          </button>
        </>
      ) : (
        <>
          <span className="flex-1 font-mono text-sm text-white" data-testid={`${testId}-value`}>{value}</span>
          <button type="button" onClick={onEdit} data-testid={`${testId}-edit`}
            className="inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase bg-neutral-800 hover:bg-neutral-700 border border-neutral-600 text-neutral-200 px-2.5 py-1 rounded-full">
            <Edit3 size={11} /> {editLabel}
          </button>
        </>
      )}
    </div>
  );
}
